import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Phone, Mail, Globe, ArrowRight, Building2, Users, Star, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import { AutocompleteInput, UnifiedSearchInput } from '../components/AutocompleteInput';
import { useAuth, API } from '../App';

const ProviderCard = ({ provider }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 card-hover" data-testid={`provider-card-${provider.provider_id}`}>
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
          {provider.logo_url ? (
            <img src={provider.logo_url} alt={provider.business_name} className="w-full h-full object-cover rounded-xl" />
          ) : (
            <Building2 className="h-8 w-8 text-blue-600" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <span className="overline text-blue-600 text-[10px]">{provider.category}</span>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-1 truncate">
            {provider.business_name}
          </h3>
          <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-2">
            <MapPin className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">{provider.service_area}</span>
          </div>
          {/* Rating Display */}
          {(provider.average_rating > 0 || provider.review_count > 0) && (
            <div className="flex items-center gap-1 text-sm">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span className="font-medium text-slate-700">{(provider.average_rating || 0).toFixed(1)}</span>
              <span className="text-slate-500">({provider.review_count || 0})</span>
            </div>
          )}
        </div>
      </div>
      
      <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-2 mt-3">
        {provider.bio}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {provider.phone && (
          <a href={`tel:${provider.phone}`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-700">
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">{provider.phone}</span>
          </a>
        )}
        {provider.email && (
          <a href={`mailto:${provider.email}`} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-700">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline truncate max-w-[120px]">{provider.email}</span>
          </a>
        )}
        {provider.website && (
          <a href={provider.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-blue-700">
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">Website</span>
          </a>
        )}
      </div>

      <Button asChild className="w-full bg-blue-700 hover:bg-blue-800 rounded-full">
        <Link to={`/providers/${provider.provider_id}`}>
          View Profile
          <ArrowRight className="h-4 w-4 ml-2" />
        </Link>
      </Button>
    </div>
  );
};

export default function DirectoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || '',
    service_area: searchParams.get('area') || '',
    search: searchParams.get('q') || ''
  });

  const categories = [
    'Realtor', 'Contractor', 'Hard Money Lender', 'Private Lender',
    'Title Company', 'Inspector', 'Property Manager', 'Insurance Provider', 'Attorney'
  ];

  // Location options for autocomplete
  const [serviceAreaOptions, setServiceAreaOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState({ cities: [], states: [], zip_codes: [] });
  
  useEffect(() => {
    fetchProviders();
  }, [filters]);

  // Fetch service area options for autocomplete
  useEffect(() => {
    const fetchServiceAreas = async () => {
      try {
        const response = await axios.get(`${API}/providers/autocomplete/locations`);
        setServiceAreaOptions(response.data.service_areas || []);
      } catch (error) {
        console.error('Failed to fetch service areas');
      }
    };
    fetchServiceAreas();
  }, []);

  // Fetch location options for unified search
  useEffect(() => {
    const fetchLocationOptions = async () => {
      try {
        // Use deals location data for suggestions since providers have free-text service_area
        const response = await axios.get(`${API}/deals/autocomplete/locations`);
        setLocationOptions({
          cities: response.data.cities || [],
          states: response.data.states || [],
          zip_codes: response.data.zip_codes || []
        });
      } catch (error) {
        console.error('Failed to fetch location options');
      }
    };
    fetchLocationOptions();
  }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.service_area) params.append('service_area', filters.service_area);
      if (filters.search) params.append('search', filters.search);
      
      const response = await axios.get(`${API}/providers?${params.toString()}`);
      setProviders(response.data);
    } catch (error) {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key === 'service_area' ? 'area' : key === 'search' ? 'q' : key, value);
    } else {
      newParams.delete(key === 'service_area' ? 'area' : key === 'search' ? 'q' : key);
    }
    setSearchParams(newParams);
  };

  // Handle unified search selection
  const handleUnifiedSearch = (searchValue, searchType) => {
    handleFilterChange('search', searchValue);
  };

  // Group providers by category for display
  const groupedProviders = providers.reduce((acc, provider) => {
    if (!acc[provider.category]) {
      acc[provider.category] = [];
    }
    acc[provider.category].push(provider);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <AuthGate title="Sign Up Free to Browse the Directory">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="max-w-2xl">
            <span className="overline text-blue-700 mb-3 block">Service Provider Directory</span>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4" data-testid="directory-title">
              Find Investor-Friendly Professionals
            </h1>
            <p className="text-lg text-slate-600">
              Connect with trusted realtors, contractors, lenders, and more who understand real estate investing.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-8">
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium text-slate-700">Filters</span>
            {(filters.search || filters.category || filters.service_area) && (
              <button
                onClick={() => {
                  setFilters({ category: '', service_area: '', search: '' });
                  setSearchParams({});
                }}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-600 transition-colors"
                data-testid="clear-all-filters"
              >
                <X className="h-4 w-4" />
                Clear All
              </button>
            )}
          </div>
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 z-10 pointer-events-none" />
              <UnifiedSearchInput
                value={filters.search}
                onChange={handleUnifiedSearch}
                locationOptions={locationOptions}
                placeholder="Search by name, city, state, or ZIP..."
                className="pl-12 h-12 pr-10"
                data-testid="directory-search"
              />
            </div>
            <Select value={filters.category || "all"} onValueChange={(v) => handleFilterChange('category', v === "all" ? '' : v)}>
              <SelectTrigger className="w-full md:w-56 h-12" data-testid="directory-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="w-full md:w-64">
              <AutocompleteInput
                value={filters.service_area}
                onChange={(v) => handleFilterChange('service_area', v)}
                options={serviceAreaOptions}
                placeholder="Type city or state..."
                className="h-12"
                data-testid="directory-area-filter"
              />
            </div>
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-8">
          <button
            onClick={() => handleFilterChange('category', '')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              !filters.category 
                ? 'bg-blue-700 text-white' 
                : 'bg-white border border-slate-200 text-slate-700 hover:border-blue-300'
            }`}
          >
            All
          </button>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => handleFilterChange('category', cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filters.category === cat 
                  ? 'bg-blue-700 text-white' 
                  : 'bg-white border border-slate-200 text-slate-700 hover:border-blue-300'
              }`}
              data-testid={`category-pill-${cat.toLowerCase().replace(/ /g, '-')}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-16 h-16 bg-slate-200 rounded-xl animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-slate-200 rounded animate-pulse w-1/3" />
                    <div className="h-5 bg-slate-200 rounded animate-pulse" />
                    <div className="h-4 bg-slate-200 rounded animate-pulse w-2/3" />
                  </div>
                </div>
                <div className="h-12 bg-slate-100 rounded animate-pulse mb-4" />
                <div className="h-10 bg-slate-200 rounded-full animate-pulse" />
              </div>
            ))}
          </div>
        ) : providers.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No providers found</h3>
            <p className="text-slate-600 mb-6">Try adjusting your search or filters</p>
            <Button 
              variant="outline" 
              onClick={() => {
                setFilters({ category: '', service_area: '', search: '' });
                setSearchParams({});
              }}
            >
              Clear Filters
            </Button>
          </div>
        ) : filters.category ? (
          // Single category view
          <div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">
              {filters.category}s ({providers.length})
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {providers.map(provider => (
                <ProviderCard key={provider.provider_id} provider={provider} />
              ))}
            </div>
          </div>
        ) : (
          // Grouped by category
          <div className="space-y-12">
            {Object.entries(groupedProviders).map(([category, categoryProviders]) => (
              <div key={category}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading text-xl font-semibold text-slate-900">
                    {category}s ({categoryProviders.length})
                  </h2>
                  <Button 
                    variant="ghost" 
                    onClick={() => handleFilterChange('category', category)}
                    className="text-blue-700"
                  >
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categoryProviders.slice(0, 3).map(provider => (
                    <ProviderCard key={provider.provider_id} provider={provider} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-slate-900 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Are You a Service Provider?
          </h2>
          <p className="text-slate-400 mb-6 max-w-xl mx-auto">
            Join our directory and connect with thousands of real estate investors looking for professionals like you.
          </p>
          <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 rounded-full px-8" data-testid="join-directory-btn">
            <Link to="/signup?role=provider">
              Join the Directory Free
              <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
      </AuthGate>

      <Footer />
    </div>
  );
}
